/**
 * TGM Motion — Global animation system for The Golden Mods
 * Vanilla JS, no dependencies, 60fps target
 */
(function () {
  'use strict';

  var reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isTouch = matchMedia('(pointer: coarse)').matches;
  var isMobile = matchMedia('(max-width: 750px)').matches;
  var desktopOnly = !isMobile && !isTouch;

  /* ── Utilities ───────────────────────────────────────────── */

  function easeOutExpo(t) {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
  }

  function formatNumber(n) {
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ── 1. Scroll Reveal ────────────────────────────────────── */

  function initScrollReveal() {
    var items = document.querySelectorAll('.tgm-fade-in');
    if (!items.length) return;

    // Handle stagger parents first
    document.querySelectorAll('[data-stagger-children]').forEach(function (parent) {
      var children = parent.querySelectorAll('.tgm-fade-in');
      children.forEach(function (child, i) {
        if (!child.hasAttribute('data-delay')) {
          child.setAttribute('data-delay', String(i * 100));
        }
      });
    });

    if (reducedMotion) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.getAttribute('data-delay') || '0', 10);
        setTimeout(function () { el.classList.add('is-visible'); }, delay);
        observer.unobserve(el);
      });
    }, { threshold: 0.15 });

    items.forEach(function (el) { observer.observe(el); });
  }

  /* ── 2. Parallax ─────────────────────────────────────────── */

  function initParallax() {
    if (!desktopOnly || reducedMotion) return;

    var els = document.querySelectorAll('[data-parallax]');
    if (!els.length) return;

    var active = [];
    var ticking = false;

    var viewObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var rec = active.find(function (a) { return a.el === entry.target; });
        if (rec) rec.visible = entry.isIntersecting;
      });
    }, { rootMargin: '100px' });

    els.forEach(function (el) {
      var speed = parseFloat(el.getAttribute('data-parallax')) || 0.3;
      var item = { el: el, speed: speed, visible: false };
      active.push(item);
      viewObserver.observe(el);
    });

    function tick() {
      var scrollY = window.scrollY;
      active.forEach(function (item) {
        if (!item.visible) return;
        var rect = item.el.getBoundingClientRect();
        var center = rect.top + rect.height * 0.5;
        var offset = (center - window.innerHeight * 0.5) * item.speed;
        item.el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
      });
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(tick); }
    }, { passive: true });
  }

  /* ── 3. Counter Animation ────────────────────────────────── */

  function initCounters() {
    var els = document.querySelectorAll('[data-counter]');
    if (!els.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        observer.unobserve(el);
        animateCounter(el);
      });
    }, { threshold: 0.15 });

    els.forEach(function (el) { observer.observe(el); });
  }

  function animateCounter(el) {
    var raw = el.getAttribute('data-counter') || el.textContent.replace(/[^\d]/g, '');
    var target = parseInt(raw, 10) || 0;
    var suffix = el.getAttribute('data-counter-suffix') || '';
    var duration = 2000;
    var start = performance.now();

    if (reducedMotion) {
      el.textContent = formatNumber(target) + suffix;
      return;
    }

    el.style.willChange = 'contents';

    function step(now) {
      var t = Math.min((now - start) / duration, 1);
      var value = Math.round(easeOutExpo(t) * target);
      el.textContent = formatNumber(value) + suffix;
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        el.style.willChange = '';
      }
    }

    requestAnimationFrame(step);
  }

  /* ── 4. Marquee ──────────────────────────────────────────── */

  function initMarquee() {
    var tracks = document.querySelectorAll('.tgm-marquee__track');
    tracks.forEach(function (track) {
      var speed = parseFloat(track.getAttribute('data-speed')) || 30;
      var content = track.innerHTML;
      // Clone enough to fill seamlessly
      track.insertAdjacentHTML('beforeend', content);

      var width = track.scrollWidth / 2;
      var dur = (width / speed).toFixed(2);

      track.style.cssText =
        'display:flex;white-space:nowrap;animation:tgmMarquee ' + dur + 's linear infinite;';

      // Inject keyframes once
      if (!document.getElementById('tgm-marquee-css')) {
        var style = document.createElement('style');
        style.id = 'tgm-marquee-css';
        style.textContent =
          '@keyframes tgmMarquee{0%{transform:translate3d(0,0,0)}' +
          '100%{transform:translate3d(-50%,0,0)}}';
        document.head.appendChild(style);
      }

      // Pause on hover
      var parent = track.closest('.tgm-marquee') || track.parentElement;
      parent.addEventListener('mouseenter', function () {
        track.style.animationPlayState = 'paused';
      });
      parent.addEventListener('mouseleave', function () {
        track.style.animationPlayState = 'running';
      });
    });
  }

  /* ── 5. Image Reveal ─────────────────────────────────────── */

  function initImageReveal() {
    var els = document.querySelectorAll('[data-image-reveal]');
    if (!els.length) return;

    if (reducedMotion) {
      els.forEach(function (el) {
        el.style.opacity = '1';
        el.style.transform = 'scale(1)';
      });
      return;
    }

    els.forEach(function (el) {
      el.style.opacity = '0';
      el.style.transform = 'scale(1.1)';
      el.style.transition = 'opacity 1.2s ease, transform 1.2s ease';
    });

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        el.style.willChange = 'transform, opacity';
        requestAnimationFrame(function () {
          el.style.opacity = '1';
          el.style.transform = 'scale(1)';
        });
        el.addEventListener('transitionend', function handler() {
          el.style.willChange = '';
          el.removeEventListener('transitionend', handler);
        });
        observer.unobserve(el);
      });
    }, { threshold: 0.15 });

    els.forEach(function (el) { observer.observe(el); });
  }

  /* ── 6. Hover Tilt ───────────────────────────────────────── */

  function initHoverTilt() {
    if (!desktopOnly || reducedMotion) return;

    var els = document.querySelectorAll('.tgm-tilt');
    els.forEach(function (el) {
      el.style.transition = 'transform 0.3s ease';

      var parent = el.parentElement;
      if (parent) parent.style.perspective = '1000px';

      var ac = new AbortController();

      el.addEventListener('mousemove', function (e) {
        var rect = el.getBoundingClientRect();
        var x = (e.clientX - rect.left) / rect.width - 0.5;
        var y = (e.clientY - rect.top) / rect.height - 0.5;
        var rotY = (x * 8).toFixed(2);   // max ~4deg
        var rotX = (-y * 8).toFixed(2);
        el.style.transform =
          'rotateX(' + rotX + 'deg) rotateY(' + rotY + 'deg)';
      }, { signal: ac.signal });

      el.addEventListener('mouseleave', function () {
        el.style.transform = 'rotateX(0) rotateY(0)';
      }, { signal: ac.signal });
    });
  }

  /* ── 7. Cursor Parallax ──────────────────────────────────── */

  function initCursorParallax() {
    if (!desktopOnly || reducedMotion) return;

    var sections = document.querySelectorAll('[data-cursor-parallax]');
    sections.forEach(function (section) {
      var children = section.querySelectorAll('[data-cursor-shift]');
      if (!children.length) return;

      var targetX = 0, targetY = 0, curX = 0, curY = 0;
      var running = false;

      function lerp(a, b, f) { return a + (b - a) * f; }

      function tick() {
        curX = lerp(curX, targetX, 0.08);
        curY = lerp(curY, targetY, 0.08);

        children.forEach(function (child) {
          var factor = parseFloat(child.getAttribute('data-cursor-shift')) || 1;
          var dx = (curX * 15 * factor).toFixed(1);
          var dy = (curY * 15 * factor).toFixed(1);
          child.style.transform = 'translate3d(' + dx + 'px,' + dy + 'px,0)';
        });

        if (Math.abs(targetX - curX) > 0.01 || Math.abs(targetY - curY) > 0.01) {
          requestAnimationFrame(tick);
        } else {
          running = false;
        }
      }

      section.addEventListener('mousemove', function (e) {
        var rect = section.getBoundingClientRect();
        targetX = (e.clientX - rect.left) / rect.width - 0.5;
        targetY = (e.clientY - rect.top) / rect.height - 0.5;
        if (!running) { running = true; requestAnimationFrame(tick); }
      }, { passive: true });

      section.addEventListener('mouseleave', function () {
        targetX = 0;
        targetY = 0;
        if (!running) { running = true; requestAnimationFrame(tick); }
      });
    });
  }

  /* ── 8. Product gallery height fix ──────────────────────────
     The native product media slider lays every slide out in normal
     flow (not absolutely stacked), so the slider's own box height is
     the max height across ALL slides, not just the visible one. When
     product photos have mixed aspect ratios (e.g. a tall lifestyle
     shot next to a square dial close-up), switching to — or simply
     landing on — a shorter slide leaves blank space below it, sized
     to the tallest slide in the set. This pins the slider's height to
     whichever slide is actually active/visible instead. */

  function initProductGalleryHeightFix() {
    var sliders = document.querySelectorAll('.product-media-slider');
    if (!sliders.length) return;

    sliders.forEach(function (slider) {
      function activeSlide() {
        return slider.querySelector('.slider__slide.is-active') || slider.querySelector('.slider__slide');
      }

      function syncHeight() {
        var active = activeSlide();
        if (!active) return;
        var h = active.getBoundingClientRect().height;
        if (h > 0) {
          slider.style.height = h + 'px';
          slider.style.overflow = 'hidden';
        }
      }

      syncHeight();
      window.addEventListener('load', syncHeight);
      window.addEventListener('resize', syncHeight);

      var classObserver = new MutationObserver(syncHeight);
      slider.querySelectorAll('.slider__slide').forEach(function (slide) {
        classObserver.observe(slide, { attributes: true, attributeFilter: ['class'] });
      });

      slider.querySelectorAll('img').forEach(function (img) {
        if (img.complete) return;
        img.addEventListener('load', syncHeight, { once: true });
      });

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:section:load', syncHeight);
      }
    });
  }

  /* ── Bootstrap ───────────────────────────────────────────── */

  document.addEventListener('DOMContentLoaded', function () {
    initScrollReveal();
    initParallax();
    initCounters();
    initMarquee();
    initImageReveal();
    initHoverTilt();
    initCursorParallax();
    initProductGalleryHeightFix();
  });
})();
